import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Gamepad2, Mail, Lock, User, ArrowLeft, Check, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { toast } from 'sonner';

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next') || '/dashboard';
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingVerify, setPendingVerify] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await register(email, password, name);
      if (res?.verification_required) {
        setPendingVerify(true);
        if (res.email_sent === false) toast.error("We couldn't send the email right away — use Resend below in a minute");
        else toast.success('Almost there! Check your inbox to verify your email.');
        return;
      }
      toast.success('Account created! Welcome to PlayMold!');
      // Guest-flow: honor ?next= so users return to what they were doing
      // (e.g., signed up from a Remix prompt → lands back on Gallery ready to remix)
      navigate(nextPath.startsWith('/') ? nextPath : '/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    try {
      await axios.post(`${API}/auth/resend-verification`, { email }, { withCredentials: true });
      toast.success('Verification email sent — check your inbox');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not resend yet'); }
  };

  if (pendingVerify) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-[#0A0A0C] border border-[#1E1E24] rounded-2xl p-8 text-center" data-testid="verify-pending-card">
          <Mail className="w-10 h-10 text-[#06B6D4] mx-auto mb-4" />
          <h1 className="text-xl font-black text-white mb-2">Check your inbox</h1>
          <p className="text-sm text-zinc-400 mb-6">We sent a verification link to <span className="text-white font-semibold">{email}</span>. Click it to activate your account, then log in.</p>
          <div className="space-y-2">
            <Button onClick={resendVerification} variant="outline" className="w-full bg-transparent border-[#1E1E24] text-zinc-300 hover:text-white" data-testid="resend-verification-btn">Resend email</Button>
            <Button onClick={() => navigate('/login')} className="w-full bg-[#06B6D4] text-black hover:bg-[#0891B2]" data-testid="go-login-btn">Go to Log in</Button>
          </div>
        </div>
      </div>
    );
  }

  const benefits = [
    "Create unlimited game projects",
    "AI-powered game generation",
    "Real-time 3D editor",
    "Publish and share games"
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex" data-testid="register-page">
      {/* Left - Visual */}
      <div className="hidden lg:flex flex-1 bg-[#0A0A0B] items-center justify-center p-16 relative overflow-hidden border-r border-[#1E1E24]">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative z-10 max-w-md">
          <h2 className="text-4xl font-black tracking-tighter text-white mb-4">
            Start Building Games Today
          </h2>
          <p className="text-zinc-400 mb-8">
            Join thousands of creators making games with AI. No coding required.
          </p>
          <div className="space-y-3">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-[#22C55E]" />
                </div>
                <span className="text-zinc-300 text-sm">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 md:px-16 lg:px-24 relative">
        <div className="absolute inset-0 hero-glow opacity-30" />
        
        <div className="relative z-10 max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-sm bg-[#06B6D4]/20 border border-[#06B6D4]/50 flex items-center justify-center">
              <Gamepad2 className="w-6 h-6 text-[#06B6D4]" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-white">PlayMold</h1>
              <p className="text-[10px] text-zinc-500 font-mono tracking-widest uppercase">AI Game Creator</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-white mb-2">Create Account</h2>
          <p className="text-zinc-400 mb-8">Start building your first game</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input id="name" type="text" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)}
                  className="pl-10 bg-transparent border-[#1E1E24] focus:border-[#06B6D4] text-white" data-testid="name-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-transparent border-[#1E1E24] focus:border-[#06B6D4] text-white" data-testid="email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Min 6 characters" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 bg-transparent border-[#1E1E24] focus:border-[#06B6D4] text-white" data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300" data-testid="password-toggle">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90" disabled={loading} data-testid="register-submit-btn">
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-zinc-500">
            Already have an account? <Link to={`/login${nextPath !== '/dashboard' ? `?next=${encodeURIComponent(nextPath)}` : ''}`} className="text-[#06B6D4] hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
